import { useHandler } from "@chatbotgang/etude/react/useHandler";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useSnackbar } from "notistack";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { Button } from "@/components/Button";
import { Tag } from "@/components/Tag";
import { Typography } from "@/components/Typography";
import { interlude } from "@/interlude";

const emailSchema = z.string().email();

const styles = {
  card: {
    border: `1px solid ${theme.colors.neutral020}`,
    borderRadius: "12px",
  },
  cardContent: {
    padding: "24px",
    "&:last-child": {
      paddingBottom: "24px",
    },
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  mailIcon: {
    color: theme.colors.staticFgBody,
    fontSize: "20px",
  },
  inputRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
  },
  emailInput: {
    flex: 1,
    maxWidth: "360px",
  },
  chipsContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    py: "48px",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

export const NotificationRecipients: React.FC =
  function NotificationRecipients() {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const notifyEmailsQuery = interlude.billing.useGetNotifyEmails();

    const updateEmailsMutation = interlude.billing.useUpdateNotifyEmails(
      undefined,
      {
        onError: () => {
          enqueueSnackbar(t("common.apiError.unexpectedError"), {
            variant: "error",
          });
        },
      },
    );

    const [inputValue, setInputValue] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    const recipients = useMemo(
      () => notifyEmailsQuery.data?.notifyEmails ?? [],
      [notifyEmailsQuery.data?.notifyEmails],
    );

    const handleInputChange = useHandler<
      React.ComponentProps<typeof TextField>["onChange"]
    >((e) => {
      setInputValue(e.target.value);
      if (errorMessage) {
        setErrorMessage("");
      }
    });

    const handleAdd = useHandler(() => {
      const trimmed = inputValue.trim();
      if (!trimmed) return;

      if (!emailSchema.safeParse(trimmed).success) {
        setErrorMessage(
          t("billing.alertSetting.notificationRecipients.invalidEmail"),
        );
        return;
      }

      if (recipients.includes(trimmed)) {
        setErrorMessage(
          t("billing.alertSetting.notificationRecipients.duplicateEmail"),
        );
        return;
      }

      updateEmailsMutation.mutate({ notifyEmails: [...recipients, trimmed] });
      setInputValue("");
      setErrorMessage("");
    });

    const handleKeyDown = useHandler(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
          e.preventDefault();
          handleAdd();
        }
      },
    );

    const handleDelete = useHandler((email: string) => {
      updateEmailsMutation.mutate({
        notifyEmails: recipients.filter((r) => r !== email),
      });
    });

    const isMutating = updateEmailsMutation.isPending;

    const emailChips: React.ReactNode = useMemo(
      () =>
        recipients.length > 0 ? (
          <Box sx={styles.chipsContainer}>
            {recipients.map((email) => (
              <Tag
                key={email}
                label={email}
                onDelete={() => handleDelete(email)}
                disabled={isMutating}
              />
            ))}
          </Box>
        ) : null,
      [recipients, handleDelete, isMutating],
    );

    if (notifyEmailsQuery.isPending) {
      return (
        <Card variant="outlined" sx={styles.card}>
          <CardContent sx={styles.cardContent}>
            <Box sx={styles.loadingContainer}>
              <CircularProgress />
            </Box>
          </CardContent>
        </Card>
      );
    }

    if (notifyEmailsQuery.isError) {
      return (
        <Card variant="outlined" sx={styles.card}>
          <CardContent sx={styles.cardContent}>
            <Box sx={styles.loadingContainer}>
              <Typography variant="body">
                {t("billing.error.generic")}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card variant="outlined" sx={styles.card}>
        <CardContent sx={styles.cardContent}>
          <Stack gap="16px">
            <Box sx={styles.titleRow}>
              <MailOutlineRoundedIcon sx={styles.mailIcon} />
              <Typography variant="h3">
                {t("billing.alertSetting.notificationRecipients.title")}
              </Typography>
            </Box>
            <Typography variant="body">
              {t("billing.alertSetting.notificationRecipients.description")}
            </Typography>
            <Box sx={styles.inputRow}>
              <TextField
                size="small"
                placeholder={t(
                  "billing.alertSetting.notificationRecipients.placeholder",
                )}
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                error={!!errorMessage}
                helperText={errorMessage}
                disabled={isMutating}
                sx={styles.emailInput}
              />
              <Button
                variant="secondary"
                onClick={handleAdd}
                disabled={isMutating}
              >
                {t("billing.alertSetting.notificationRecipients.addButton")}
              </Button>
            </Box>
            {emailChips}
          </Stack>
        </CardContent>
      </Card>
    );
  };
